import * as React from "react";
import { Link, RouteComponentProps } from "react-router-dom";

import BulkProjectActions from "@app/home/bulk-project-actions/BulkProjectActions";
import ProjectTable from "@app/home/ProjectTable";
import WrappedProjectTile from "@app/home/ProjectTile";
import { Message, MessageType, MultiProjectRunStatusChangePayload, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { ProjectDashboardModel, ProjectService, ProjectTileData } from "@app/projects";
import { FilterControls as ProjectFilterControls } from "@app/resource-filters";
import { FilterItem } from "@app/resource-filters/filter-items";
import { FilteredResource, Resource, ResourceStatus, ResourceType } from "@app/resources";
import { BreadcrumbTemplateEnum, ErrorBoundary, LoadingSpinner, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection, breadcrumbTemplates } from "@app/shared";
import { Unauthorized } from "@app/shared/ErrorPages";
import { RoleRestricted, Roles, UserContextProps, UserInfo, withUserContext } from "@app/user";
import { ResourceFilterSet } from "../resource-filters/filter-sets";
import DashboardSummary from "./dashboard-summary/DashboardSummary";
import { ProjectLookup, ResultLookup } from "./homeInterfaces";
import { QESButton } from "@app/shared/qes-standards";
import { FlyoutLink } from "@app/shared/Flyouts";
import EnterpriseComplianceDashboardService from "../overviews/enterprise-compliance-dashboard/enterpriseComplianceDashboardService";
import { ModalLink } from "../shared/Modals";
import { parseQuerystring } from "@app/shared/utility";
import { XhrReadyStateEnum } from "@app/shared/xhrReadyStateEnum";
import { datadogRum } from "@datadog/browser-rum";

interface HomeState {
  clientGapsRunning: boolean;
  dualMembershipToggles: Set<number>;
  ecdJobsQueued: boolean;
  filteredProjectResources: FilteredResource[];
  filtersLoaded: boolean;
  loadingResults: boolean;
  projectDashboardModel: ProjectDashboardModel;
  newFilterItems: FilterItem[];
  projectLookup: ProjectLookup;
  resourcesLoaded: boolean;
  resultLookup: ResultLookup;
  showingArchived: boolean;
  showingHidden: boolean;
  view: "List" | "Tile";
}

type HomeProps = UserContextProps & NotificationContextProps & RouteComponentProps;

class Home extends React.Component<HomeProps, HomeState> {
  private readonly projectService = new ProjectService();
  private readonly enterpriseComplianceDashboardService = new EnterpriseComplianceDashboardService();
  private projectResources: FilteredResource[] = [];
  private initialLoad = true;
  private projectDashboardModelRequest: JQueryXHR = null;

  constructor(props: HomeProps) {
    super(props);

    this.state = {
      clientGapsRunning: false,
      dualMembershipToggles: new Set<number>(),
      ecdJobsQueued: false,
      filteredProjectResources: [],
      filtersLoaded: false,
      loadingResults: false,
      newFilterItems: [],
      projectDashboardModel: {
        countyCount: 0,
        gapCount: 0,
        percentGaps: 0,
        projectCount: 0,
        projectTileAggregates: [],
        totalCount: 0,
      },
      projectLookup: {},
      resourcesLoaded: false,
      resultLookup: {},
      showingArchived: false,
      showingHidden: false,
      view: "List",
    };
  }

  public async componentDidMount() {
    // Check query string to determine which projects we load
    const qs = parseQuerystring(this.props.location.search);
    const qsArchived = qs && !!qs.archived && qs.archived === "true";
    const qsHidden = qs && !!qs.hidden && qs.hidden === "true";

    try {
      const resources = await this.projectService.getProjectResources(qsArchived ? ResourceStatus.Archived : ResourceStatus.Active, true, qsHidden);

      if (resources.length === 0) {
        datadogRum.addError("No project resources found.");
      }

      const projectLookup: ProjectLookup = {};

      resources.forEach((r) => {
        this.projectResources.push({ filterPath: "", resourceId: r.resourceId, serviceAreaRegionSets: [], specialtyGroupCodes: [] });
        projectLookup[r.resourceId] = r;
      });

      // default to tile view
      const view = localStorage.getItem(this.viewStorageKey(this.props.user)) === "List" ? "List" : "Tile";

      if (qsArchived) {
        this.setState({
          filteredProjectResources: this.projectResources,
          filtersLoaded: true, //archived have no filters, therefore consider them loaded already
          loadingResults: true,
          projectLookup,
          resourcesLoaded: true,
          showingArchived: true,
          showingHidden: false,
          view,
        }, async () => {
          this.initialLoad = false;
          await this.loadResults();
        });
      } else if (qsHidden) {
        this.setState({
          filteredProjectResources: this.projectResources,
          filtersLoaded: true, //hidden have no filters, therefore consider them loaded already
          loadingResults: true,
          projectLookup,
          resourcesLoaded: true,
          showingArchived: false,
          showingHidden: true,
          view,
        }, async () => {
          this.initialLoad = false;
          await this.loadResults();
        });
      } else {
        this.setState({
          projectLookup,
          resourcesLoaded: true,
          showingArchived: false,
          showingHidden: false,
          view,
        }, () => this.initialLoad = false);
      }

      this.props.subscribe(this.handleNotifications);
    } catch (e: unknown) {
      datadogRum.addError(`Error loading project resources: ${e}`);
    }
  }

  public async componentDidUpdate(prevProps: HomeProps, prevState: HomeState) {
    if (prevState.showingArchived !== this.state.showingArchived || prevState.showingHidden !== this.state.showingHidden) {
      this.projectResources = [];

      const resources = await this.projectService.getProjectResources(this.state.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active, true, this.state.showingHidden);

      const projectLookup: ProjectLookup = {};

      resources.forEach((r) => {
        this.projectResources.push({ filterPath: "", resourceId: r.resourceId, serviceAreaRegionSets: [], specialtyGroupCodes: [] });
        projectLookup[r.resourceId] = r;
      });

      if (this.state.showingArchived) {
        this.setState({
          filteredProjectResources: this.projectResources,
          filtersLoaded: true, //archived have no filters, therefore consider them loaded already
          loadingResults: true,
          projectLookup,
          resourcesLoaded: true,
        }, async () => {
          this.initialLoad = false;
          await this.loadResults();
        });
      } else if (this.state.showingHidden) {
        this.setState({
          filteredProjectResources: this.projectResources,
          filtersLoaded: true, //hidden have no filters, therefore consider them loaded already
          loadingResults: true,
          projectLookup,
          resourcesLoaded: true,
        }, async () => {
          this.initialLoad = false;
          await this.loadResults();
        });
      } else {
        this.setState({
          loadingResults: true,
          projectLookup,
          resourcesLoaded: true,
        }, () => this.initialLoad = false);
      }
    }
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
    if (!!this.projectDashboardModelRequest?.abort) {
      this.projectDashboardModelRequest.abort();
    }
  }

  public render() {
    if (this.props.user) {
      return this.renderAuthorized(this.props.user);
    }

    return (<Unauthorized />);
  }

  public renderAuthorized(user: UserInfo) {
    const pageTitle = this.state.showingArchived ? "Archived Projects" : this.state.showingHidden ? "Hidden Projects" : "Project Dashboard";
    const projectResources: Resource[] = [];
    if (Object.keys(this.state.projectLookup).length !== 0) {
      for (const filteredResource of this.state.filteredProjectResources) {
        if (filteredResource && this.state.projectLookup[filteredResource.resourceId])
          projectResources.push(this.state.projectLookup[filteredResource.resourceId]);
      }
    }

    projectResources.sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();

      if (nameA < nameB) { return -1; }
      if (nameA > nameB) { return 1; }
      return 0;
    });

    const dashboardFilters = JSON.parse(localStorage.getItem(`${user.userId}${ResourceType.Project}_filters`)) as ResourceFilterSet;
    const dashboardHasSpecialtyFilter = dashboardFilters && (dashboardFilters.filterGroups.concat(dashboardFilters.pendingChanges)).some((item) => item.filters.some((filter) => filter.namespacePath.includes("Specialties")));

    return (
      <ErrorBoundary user={user}>
        <Page documentTitle="Network Assessment">
          <PageInner>
            <PageHeader title={pageTitle} disableHR={true}>
              <PageHeaderLeft>
                <span className="btn-group">
                  <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN]}>
                    <span>
                      <Link
                        to={{
                          pathname: `/resources/share`,
                          state: {
                            breadcrumbItems: breadcrumbTemplates[BreadcrumbTemplateEnum.NetworkAssessment],
                            isSingleResource: false,
                            resourceDescription: "Project",
                            resourceTypes: [ResourceType.Project],
                          },
                        }}
                        data-testid="home__share-link"
                      >
                        <i className="fa fa-share" />
                        Share
                      </Link>
                    </span>
                    {!this.state.showingArchived && !this.state.showingHidden &&
                      (
                        <>
                          <span className="ml5">
                            <FlyoutLink
                              content={{
                                body: this.getBulkProjectActionsContent(),
                                title: "Project Actions",
                              }}
                              testId="bulk-project-actions"
                              classNames="p-0 font-weight-normal"
                            >
                              <i className="fa fa-rocket" /> Project Actions
                            </FlyoutLink>
                          </span>
                        </>
                      )
                    }
                    {(this.state.showingArchived || this.state.showingHidden) &&
                      (
                        <>
                          <span className="ml5 disabled" data-testid="side-panel-link__bulk-project-actions-side-panel-link-button">
                            <i className="fa fa-rocket" /> Project Actions
                          </span>
                        </>
                      )
                    }
                  </RoleRestricted>
                  {!this.state.showingArchived &&
                    (
                      <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN]}>
                        <span className="ml5">
                          <QESButton
                            qesType="none"
                            className="p-0 font-weight-normal"
                            qesTestId="home__archived-button"
                            onClick={this.setArchiveMode}
                            disabled={this.state.loadingResults || this.initialLoad}
                          >
                            <i className="fa fa-angle-double-up" /> Archived
                          </QESButton>
                        </span>
                      </RoleRestricted>
                    )
                  }
                  {this.state.showingArchived &&
                    (
                      <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN]}>
                        <span className="ml5">
                          <QESButton
                            qesType="none"
                            className="p-0 font-weight-normal"
                            qesTestId="home__unarchived-button"
                            onClick={this.setArchiveMode}
                            disabled={this.state.loadingResults || this.initialLoad}
                          >
                            <i className="fa fa-angle-double-up" /> Unarchived
                          </QESButton>
                        </span>
                      </RoleRestricted>
                    )
                  }
                  {!this.state.showingHidden &&
                    (
                      <span className="ml5">
                        <QESButton
                          qesType="none"
                          className="p-0 font-weight-normal"
                          qesTestId="home__hidden-button"
                          onClick={this.setHiddenMode}
                          disabled={this.state.loadingResults || this.initialLoad}
                        >
                          <i className="fa fa-low-vision" /> Hidden
                        </QESButton>
                      </span>
                    )
                  }
                  {this.state.showingHidden &&
                    (
                      <span className="ml5">
                        <QESButton
                          qesType="none"
                          className="p-0 font-weight-normal"
                          qesTestId="home__non-hidden-button"
                          onClick={this.setHiddenMode}
                          disabled={this.state.loadingResults || this.initialLoad}
                        >
                          <i className="fa fa-low-vision" /> Non-Hidden
                        </QESButton>
                      </span>
                    )
                  }
                  <span className="ml5">
                    <QESButton
                      qesType="none"
                      className="p-0 font-weight-normal"
                      qesTestId="home__toggle-view-button"
                      onClick={this.toggleView}
                    >
                      <i className={`fa ${this.state.view === "Tile" ? "fa-list" : "fa-th-large"}`} /> Switch to {this.state.view === "Tile" ? "List" : "Tile"} View
                    </QESButton>
                  </span>
                </span>
              </PageHeaderLeft>

              <PageHeaderRight>
                <RoleRestricted allowedRoles={[Roles.QUEST_USER, Roles.CLIENT_ADMIN, Roles.QUEST_ADMIN]}>
                  <Link to="/projects/create/1" className="qes-button qes-button-primary mt-1 mr5" type="button"><i className="fa fa-plus pr5" /> Add Project...</Link>
                  <QESButton qesTestId="home_refresh-client-gaps" qesType="primary" disabled={this.state.clientGapsRunning} onClick={() => this.handleRunClientGaps()} className="mt-1 mr5"><i className={`fa fa-refresh${this.state.clientGapsRunning ? " fa-spin" : ""} pr5`} /> Refresh Client Gaps</QESButton>
                  <RoleRestricted allowedRoles={[Roles.ECD_DEMO_ADMIN]}>
                    <ModalLink
                      classNames="qes-button qes-button-primary mt-1 mr5"
                      disabled={this.state.ecdJobsQueued}
                      content={{
                        body: <>This will queue the Enterprise Compliance Dashboard job for all active projects on this client.<br /><b>This should be used only for demo purposes and not if the client is already using ECD.</b></>,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: this.handleQueueEcdJobs,
                        confirmButtonText: "Queue ECD Jobs",
                        confirmButtonType: "primary",
                        title: "Warning",
                      }}
                      testId="home_queue-ecd-jobs-modal-link"
                    >
                      <i className={`fa fa-refresh${this.state.ecdJobsQueued ? " fa-spin" : ""} pr5`} /> Populate ECD Data
                    </ModalLink>
                    <ModalLink
                      classNames="qes-button qes-button-primary mt-1 mr5"
                      disabled={this.state.ecdJobsQueued}
                      content={{
                        body: <>This will purge the Enterprise Compliance Dashboard data for any projects which do not have it enabled.<br /><strong>This should be used only for purging ECD demo data.</strong></>,
                        cancelButtonText: "Cancel",
                        confirmButtonAction: this.handlePurgeEcdJobs,
                        confirmButtonText: "Purge ECD Demo Data",
                        confirmButtonType: "primary-danger",
                        title: "Warning",
                      }}
                      testId="home_purge-ecd-jobs-modal-link"
                    >
                      <i className={`fa fa-refresh${this.state.ecdJobsQueued ? " fa-spin" : ""} pr5`} /> Purge ECD Demo Data
                    </ModalLink>
                  </RoleRestricted>
                </RoleRestricted>
              </PageHeaderRight>
            </PageHeader>
            {(this.state.resourcesLoaded && !this.state.showingArchived && !this.state.showingHidden) && (
              <PageSection>
                <hr className="mtb0" />
                <ProjectFilterControls
                  newFilterKeys={this.state.newFilterItems}
                  resourceType={ResourceType.Project}
                  onFilteredItemsChange={this.handleFilteredItemsChange}
                  user={user}
                />
              </PageSection>
            )}

            <PageSection className="mb10">
              <RoleRestricted allowedRoles={[Roles.DASHBOARD_SUMMARY_USER]}>
                <span>
                  <DashboardSummary
                    filteredResources={this.state.filteredProjectResources}
                    hasSpecialtyFilter={dashboardHasSpecialtyFilter}
                    handleAddFilter={(filterItem: FilterItem) => { this.setState({ newFilterItems: [filterItem] }); }}
                    loading={!this.state.filtersLoaded || this.state.loadingResults}
                    projectDashboardModel={this.state.projectDashboardModel}
                    projectResources={projectResources}
                    showingArchived={this.state.showingArchived}
                    showingHidden={this.state.showingHidden}
                    summaries={this.state.resultLookup}
                  />
                </span>
              </RoleRestricted>
            </PageSection>

            <PageSection>
              {this.state.filteredProjectResources && this.state.filteredProjectResources.length > 0 ?
                (<>
                  {this.state.view === "Tile" ?
                    (
                      <div className="row grid-space-10">
                        {projectResources
                          .map((projectResource) => {
                            const result = this.state.resultLookup[projectResource.resourceId];
                            const filteredProjectResource = this.state.filteredProjectResources.find((x) => x.resourceId === projectResource.resourceId);
                            return (
                              <WrappedProjectTile
                                {...this.props}
                                key={projectResource.resourceId}
                                initialProjectResource={projectResource}
                                initialSummary={result}
                                filteredProjectResource={filteredProjectResource}
                                onArchived={this.handleArchivedOrHidden}
                                onHidden={this.handleArchivedOrHidden}
                                onMemberSetChange={this.handleMemberSetChange}
                                onUpdateProjectOptions={this.handleUpdateSingleSummary}
                                setProjectIsReadOnly={this.setProjectIsReadOnly}
                                showSupplementaryData={this.state.dualMembershipToggles.has(result && result.projectId)}
                              />
                            );
                          })}
                      </div>
                    ) : (
                      <ProjectTable
                        {...this.props}
                        dualMembershipToggles={this.state.dualMembershipToggles}
                        filteredProjectResources={this.state.filteredProjectResources}
                        hasSpecialtyFilter={dashboardHasSpecialtyFilter}
                        loading={!this.state.filtersLoaded || this.state.loadingResults}
                        onMemberSetChange={this.handleMemberSetChange}
                        projectResourceLookup={this.state.projectLookup}
                        onArchived={this.handleArchivedOrHidden}
                        onHidden={this.handleArchivedOrHidden}
                        onUpdateProjectOptions={this.handleUpdateSingleSummary}
                        setProjectIsReadOnly={this.setProjectIsReadOnly}
                        summaries={this.state.resultLookup}
                      />
                    )
                  }
                </>)
                : (
                  this.initialLoad ? <LoadingSpinner /> : (
                    <div className="col-12">
                      <p className="lead color-faded-jade mt-3">You don&apos;t have any projects.</p>
                    </div>
                  )
                )
              }
            </PageSection>
          </PageInner>
        </Page>
      </ErrorBoundary>
    );
  }

  private handleRunClientGaps = async () => {
    this.setState({
      clientGapsRunning: true,
    });

    await this.projectService.saveClientGaps();
  }

  private handleQueueEcdJobs = async () => {
    this.setState({
      ecdJobsQueued: true,
    });

    await this.enterpriseComplianceDashboardService.queueEcdJobs();
  }

  private handlePurgeEcdJobs = async () => {
    this.setState({
      ecdJobsQueued: true,
    });

    await this.enterpriseComplianceDashboardService.purgeDemoData();
  }

  private handleUpdateSingleSummary = async (summary: ProjectTileData) => {
    const projectLookup: ProjectLookup = { ...this.state.projectLookup, [summary.projectId]: summary.resource };
    const resultLookup: ResultLookup = { ...this.state.resultLookup, [summary.projectId]: summary };

    this.setState({
      projectLookup,
      resultLookup,
    });
  }

  private handleArchivedOrHidden = async (projectId: number) => {
    this.projectResources = [...(this.projectResources.filter((resource) => resource.resourceId !== projectId))];
    const filteredProjectResources = [...(this.state.filteredProjectResources.filter((resource) => resource.resourceId !== projectId))];
    //const projectLookup: ProjectLookup = { ...this.state.projectLookup, [projectId]: undefined };
    const projectLookup = this.state.projectLookup;
    delete projectLookup[projectId];
    const resultLookup: ResultLookup = { ...this.state.resultLookup, [projectId]: undefined };

    this.setState((prevState) => ({
      ...prevState,
      filteredProjectResources,
      projectLookup,
      resultLookup,
    }));
  }

  private handleFilteredItemsChange = async (projectResources?: FilteredResource[]): Promise<void> => {
    let filteredProjectResources: FilteredResource[];

    if (projectResources === undefined) {
      filteredProjectResources = this.projectResources;
    } else {
      filteredProjectResources = [...projectResources];
    }

    this.setState((prevState) => ({
      ...prevState,
      filteredProjectResources,
      filtersLoaded: true,
      loadingResults: true,
      resultLookup: {},
    }), async () => await this.loadResults());
  }

  private handleNotifications = async (message: Message) => {
    let newLookup: ResultLookup;
    if (message) {
      switch (message.notificationType as MessageType) {
        case MessageType.ClientGapLinkResultsAvailable:
          //whatever the status, re-enable the client gaps button
          this.setState({ clientGapsRunning: false });
          break;
        case MessageType.MultiProjectRunStatusChange:
          const multiProjectPayload = message.payload as MultiProjectRunStatusChangePayload;
          newLookup = { ...this.state.resultLookup };
          multiProjectPayload.customProjectIds.forEach((projectId) => {
            if (
              !this.state.projectLookup[projectId].isShared ||
              (multiProjectPayload.isPublished && !this.state.resultLookup[projectId].hasUnpublishedResults) ||
              this.props.user.roles.includes(Roles.PREVIEW)
            ) {
              const lookupItem = {
                ...this.state.resultLookup[projectId],
                isReadOnly: multiProjectPayload.readOnly,
                runConfigurationStatus: multiProjectPayload.status,
              };
              newLookup[projectId] = lookupItem;
            }
          });
          this.setState({
            resultLookup: newLookup,
          });
          break;
      }
    }
  }

  private loadResults = async (): Promise<void> => {
    if (!this.state.filtersLoaded) {
      return;
    }
    const filteredProjectResources = this.state.filteredProjectResources;

    if (!!this.projectDashboardModelRequest?.abort && this.projectDashboardModelRequest?.readyState !== XhrReadyStateEnum.Done) {
      this.projectDashboardModelRequest.abort();
    }

    this.projectDashboardModelRequest = this.projectService.projectTileResults(filteredProjectResources,
      this.state.showingArchived ? ResourceStatus.Archived : ResourceStatus.Active, true, this.state.showingHidden)

    try {
      const projectDashboardModel: ProjectDashboardModel = (this.projectDashboardModelRequest != null) ? await this.projectDashboardModelRequest : {};
      this.projectDashboardModelRequest = null;
      const projectTileResults = projectDashboardModel && projectDashboardModel.projectTileAggregates;

      if (projectTileResults) {
        const resultLookup: ResultLookup = {};

        projectTileResults.forEach((projectTileResult) => {
          resultLookup[projectTileResult.projectId] = projectTileResult;
        });

        this.setState({
          loadingResults: false,
          projectDashboardModel,
          resultLookup,
        });
      } else {
        this.setState({
          loadingResults: false,
          projectDashboardModel: {
            countyCount: 0,
            gapCount: 0,
            percentGaps: 0,
            projectCount: 0,
            projectTileAggregates: [],
            totalCount: 0,
          },
        });
      }
    } catch (e: unknown) {
      this.projectService.handleAbortConnection(e as JQueryXHR);
    }
  }

  private setArchiveMode = () => {
    event.preventDefault();

    if (!this.state.loadingResults && !this.initialLoad) {
      this.initialLoad = true;

      this.setState((prevState): HomeState => ({
        ...prevState,
        dualMembershipToggles: new Set<number>(),
        filteredProjectResources: [],
        filtersLoaded: false,
        loadingResults: false,
        newFilterItems: [],
        projectDashboardModel: {
          countyCount: 0,
          gapCount: 0,
          percentGaps: 0,
          projectCount: 0,
          projectTileAggregates: [],
          totalCount: 0,
        },
        projectLookup: {},
        resourcesLoaded: false,
        resultLookup: {},
        showingArchived: !prevState.showingArchived,
        showingHidden: false,
      }));

      if (this.state.showingArchived) {
        this.props.history.push({
          ...this.props.history.location,
          search: "archived=false",
        });
      } else {
        this.props.history.push({
          ...this.props.history.location,
          search: "archived=true",
        });
      }
    }

  }

  private setHiddenMode = () => {
    event.preventDefault();

    if (!this.state.loadingResults && !this.initialLoad) {
      this.initialLoad = true;

      this.setState((prevState): HomeState => ({
        ...prevState,
        dualMembershipToggles: new Set<number>(),
        filteredProjectResources: [],
        filtersLoaded: false,
        loadingResults: false,
        newFilterItems: [],
        projectDashboardModel: {
          countyCount: 0,
          gapCount: 0,
          percentGaps: 0,
          projectCount: 0,
          projectTileAggregates: [],
          totalCount: 0,
        },
        projectLookup: {},
        resourcesLoaded: false,
        resultLookup: {},
        showingArchived: false,
        showingHidden: !prevState.showingHidden,
      }));

      if (this.state.showingHidden) {
        this.props.history.push({
          ...this.props.history.location,
          search: "hidden=false",
        });
      } else {
        this.props.history.push({
          ...this.props.history.location,
          search: "hidden=true",
        });
      }
    }

  }

  private toggleView = () => {
    event.preventDefault();

    const newView = this.state.view === "Tile" ? "List" : "Tile";

    localStorage.setItem(this.viewStorageKey(this.props.user), newView);

    this.setState({
      view: newView,
    });
  }

  private viewStorageKey = (user: UserInfo) => {
    return `${user.userId}_viewType`;
  }

  private handleMemberSetChange = (projectId: number, showSecondary: boolean) => {
    // just setting the new variable to the state one updates both with .add/.delete
    // and doesn't register updates to state/props
    const dualMembershipToggles = new Set<number>();
    this.state.dualMembershipToggles.forEach((id) => dualMembershipToggles.add(id));
    if (showSecondary) {
      dualMembershipToggles.add(projectId);
    } else {
      dualMembershipToggles.delete(projectId);
    }

    this.setState({
      dualMembershipToggles,
    });
  }

  private handleBulkArchive = (archivedIds: number[]) => {
    const updatedProjectLookup = { ...this.state.projectLookup };
    archivedIds.forEach((id) => {
      delete updatedProjectLookup[id];
    })
    this.setState({ loadingResults: true, projectLookup: updatedProjectLookup }, async () => await this.loadResults());
  }

  private handleBulkPublish = () => {
    this.setState({ loadingResults: true }, async () => await this.loadResults());
  }

  private getBulkProjectActionsContent = () => {
    return (
      <BulkProjectActions
        loadingResourceIds={this.state.loadingResults}
        resourceIds={Object.values(this.state.resultLookup).filter((value) => !!value).map((value: ProjectTileData) => value.resource.resourceId)}
        onArchive={this.handleBulkArchive}
        onPublish={this.handleBulkPublish}
        onSubmit={() => null}
        setProjectIsReadOnly={this.setProjectIsReadOnly}
        showPublish={true}
        showArchive={true}
      />
    );
  }

  private setProjectIsReadOnly = (projectIds: number[]) => {
    const newTileAggregates = [...this.state.projectDashboardModel.projectTileAggregates];
    projectIds.forEach((projectId) => {
      newTileAggregates.find((pta) => pta.projectId === projectId).isReadOnly = true;
    });
    const updatedDashboardModel = { ...this.state.projectDashboardModel };
    updatedDashboardModel.projectTileAggregates = newTileAggregates;
    this.setState({
      projectDashboardModel: updatedDashboardModel,
    });
  }
}

const WrappedHome = withUserContext<HomeProps>(withNotificationContext<HomeProps>(Home));

export { WrappedHome as Home }; //needed for Home.test.tsx
export default WrappedHome as React.ComponentClass<HomeProps>;
